import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { setCurrentMatterRef, clearCurrentMatterRef } from "../../lib/callNoteMatter";
import { API } from '../../config/api';
import { errorMessage } from "../../utils/errorMessage";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import CompletionStatement from "../../components/CompletionStatement";
import MatterHealthCard from "../../components/MatterHealthCard";
import MatterDeadlinesCard from "../../components/MatterDeadlinesCard";
import CclEditor from "../../components/CclEditor";
import { InfoTooltip } from "../../components/InfoTooltip";
import SDLTLinkSection from "./SDLTLinkSection";
import ActivityTimeline from "../../components/ActivityTimeline";
import CommunicationsPanel from "../../components/CommunicationsPanel";
import ReadReceipts from "../../components/ReadReceipts";
import BonsaiMate from "../../components/BonsaiMate";
import NextStepBanner from "../../components/NextStepBanner";
import MatterMLCOPanel from "../../components/compliance/MatterMLCOPanel";
import DeferredConflictBanner from "../../components/DeferredConflictBanner";
import CclSentOffSystem from "../../components/CclSentOffSystem";
import DirectorVouchPanel from "../../components/DirectorVouchPanel"; // BM-H3512
import {
  ArrowLeft, Download, FileText, User, Building2,
  PoundSterling, Clock, Mail, MapPin, Loader2,
  FlaskConical, FileJson, AlertCircle, CheckCircle2,
  Scale, Landmark, FileX, XCircle, AlertTriangle,
  ShieldAlert, FolderPlus, ExternalLink, Cloud,
  FileSpreadsheet, Search, ChevronRight, Shield, FileCheck,
  Link2, Pen, Activity, Upload, Copy, UserPlus, Plus, X as XIcon, Phone,
  Undo2, MoreVertical, Trash2, Banknote, History,
} from "lucide-react";

import { LOGO_URL } from "../../config/constants";
import { ALLOCATE_FE_EXTRA_EMAILS } from "../../config/staffConfig";
import { ownDisplayRef } from "../../lib/matterRefUtils";
import { conflictStripStatus } from "../../lib/conflictStripStatus";
import { cclStripStatus } from "../../lib/cclStripStatus";
import { sofStripStep } from "../../lib/sofStripStep";
import { useCurrentUser } from "../../hooks/useCurrentUser";
// Brief 51.1 (14 Jul 2026) — MoA History section on the matter detail
// page. Mounted at the end of the main content stream when a
// matter_reference resolves on the loaded submission.
import MoAHistoryTab from "../../components/MoAHistoryTab";

// feat/allocate-fee-earner — Dialog used by the director-only Allocate
// Fee Earner action (button cluster near the matter banner).
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

import ReportFundsBeforeCDD from "../../components/compliance/ReportFundsBeforeCDD"; // BM-H3355
// Brief 69 — header declutter. The rare / destructive actions (Call Note,
// Export JSON, Delete) move into a kebab overflow so the top bar leads with
// one primary action (Download Letter) and a couple of common edits.
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";

// BM-H4191: FORF matter type -> remortgage / transfer of equity checklist
// transaction type. These matters have no exchange, so they get the
// refinance completion checklist, not the Pre-Exchange one.
const REFINANCE_TX_FROM_Q9 = { remortgage: "remortgage", remortgage_toe: "remortgage_toe", toe_only: "toe" };
// The FORF stores the specific matter type in specific_matter_type; Q9 is the
// older field. Either one marks a remortgage or transfer of equity.
const refinanceMatterType = (submission) => {
  const d = submission?.data || {};
  return [d.specific_matter_type, submission?.specific_matter_type, d.Q9]
    .find(v => REFINANCE_TX_FROM_Q9[v]) || "";
};

function GoldenRuleProgress({ submission, carePackGates, signingStatus, feeEarnerSigApplied, docxReviewed, pipelineStatus }) {
  const sub = submission || {};
  const g = carePackGates?.gates || {};
  // pipelineStatus.pips is the SINGLE source of truth for the strip
  // (23May2026, Vanessa, BL.VEC.002.1096). If the backend has resolved
  // a stage by walking client_onboarding + conflict_checks + matter_tracker,
  // its booleans win over single-doc derived signals. Fall back to the
  // legacy carePackGates + sub.* signals only when pipelineStatus is
  // still loading or absent.
  const pips = pipelineStatus?.pips || null;

  // BM-H594: one function decides the Conflict Check chip, and it never
  // discards the backend's `cleared: true`. A cleared matter whose record is
  // not yet proven on the drive reads "Cleared, filing not confirmed", not
  // "Not started".
  const { status: ccStatus, label: ccLabel } = conflictStripStatus({
    pips,
    carePackGates,
    submission: sub,
  });

  const obGate = g.onboarding_complete || {};
  const obDetail = obGate.detail || "";
  const obSkipped = carePackGates?.ob_skipped || obDetail === "skipped";
  const obDoneFromPipeline = pips?.client_onboarding?.done === true;
  const obSentFromPipeline = pips?.client_onboarding?.sent === true;
  const obStatus = obDoneFromPipeline
    ? "complete"
    : obGate.passed
    ? "complete"
    : (obSentFromPipeline || obDetail === "link_sent" || obDetail === "pending") ? "in_progress"
    : "pending";
  const obLabel = obSkipped
    ? "Skipped (linked)"
    : (obDoneFromPipeline || obGate.passed)
    ? "Complete"
    : (obSentFromPipeline || obDetail === "link_sent") ? "Link sent"
    : obDetail === "pending" ? "In progress"
    : "Not started";

  const tfGate = g.thirdfort_complete || {};
  const tfDetail = tfGate.detail || "";
  // FIX #161: read ONLY from gates endpoint — never from submission document fields.
  // Wrong sources that must NOT be used: sub.thirdfort_completed, submissionData?.thirdfort_status
  //
  // BM-H284 (Vanessa, 4 Sep 2026): the gates endpoint is no longer the last
  // word on its own. pipelineStatus.pips.aml_thirdfort.done carries the shared
  // aml_satisfied predicate, the same one the FORF gate opens on, so it is the
  // authoritative answer and is preferred wherever the pipeline payload is
  // present. The gate is kept as the fallback for the case where this page
  // loads before pipelineStatus resolves, and it too has been narrowed in
  // routes_submissions.py to ask the same predicate, so the two now agree
  // instead of disagreeing about a regulated control.
  //
  // Why it mattered: this chip was the surface Vanessa was looking at. Both
  // the old gate expression and the tracker lit AML from Stage C signals or
  // from stage order, so a matter with no Thirdfort result at all read as
  // complete here while the review panel two panels down said the check had
  // not come back.
  const amlPipDone = pips?.aml_thirdfort?.done;
  const tfCompleted = amlPipDone === true
    || (amlPipDone === undefined && tfGate.passed === true);
  const tfStatus = tfCompleted
    ? "complete"
    : sub.thirdfort_pending || tfDetail === "pending" ? "in_progress"
    : tfDetail === "link_sent" ? "in_progress"
    : "pending";
  const tfLabel = obSkipped && tfDetail === "skipped"
    ? "Skipped (linked)"
    : tfCompleted
    ? "Verified"
    : sub.thirdfort_pending ? "Awaiting client"
    : tfDetail === "link_sent" ? "Link sent"
    : "Not started";

  // FORF strip pip: trust pipelineStatus when present (it knows about
  // forf_id / draft_forf_id / stage >= forf_draft). Fall back to the
  // legacy "always complete on this page" only when pipelineStatus is
  // absent — historical behaviour was to assume the page itself was
  // only reachable post-FORF.
  const forfDoneFromPipeline = pips?.forf_risk?.done === true;
  const forfStatus = (pips ? forfDoneFromPipeline : true) ? "complete" : "in_progress";
  const forfLabel = (pips ? forfDoneFromPipeline : true) ? "Submitted" : "In progress";

  // BM-H3703: the Source of Funds step, drawn only when the backend says
  // source of funds applies to this matter. Logic in lib/sofStripStep.
  const sof = sofStripStep(pips);
  const sofStep = sof && {
    key: "sof", label: "Source of Funds", status: sof.status, sub: sof.label, icon: Banknote,
  };

  // The CCL chip's logic lives in lib/cclStripStatus so it can be tested on
  // its own (BM-H474, BM-H3443, BM-H3512).
  const { signed: cclSigned, status: cclStatus, label: cclLabel } = cclStripStatus({
    signingStatus, submission: sub, feeEarnerSigApplied, docxReviewed, pipelineStatus,
  });

  const spCreated = sub.sharepoint_created;
  const lbStatus = sub.lawbillity_status;
  const lbCreated = lbStatus === "created";
  const stagingTransferred = sub.staging_transferred;

  const milestones = [
    obSkipped && { label: "OB Skipped", detail: carePackGates?.ob_skip_linked_ref ? `Linked: ${carePackGates.ob_skip_linked_ref}` : "Linked matter", color: "text-status-amber bg-status-amber/10 border-amber-500/30" },
    spCreated && { label: "SharePoint", detail: stagingTransferred ? "Migrated" : "Matter created", color: "text-blue-400 bg-blue-500/10 border-blue-500/30" },
    sub.sharepoint_matter_reference && { label: "Fixed Ref", detail: sub.sharepoint_matter_reference || sub.matter_reference, color: "text-teal bg-teal/10 border-teal/30" },
    lbCreated && { label: "LawBillity", detail: "Submitted", color: "text-purple-400 bg-purple-500/10 border-purple-500/30" },
    lbStatus === "needs_review" && { label: "LawBillity", detail: "Needs review", color: "text-status-amber bg-status-amber/10 border-amber-500/30" },
    lbStatus === "error" && { label: "LawBillity", detail: sub.lawbillity_dlq_id ? "Failed — admin retrying" : "Failed", color: "text-red-400 bg-red-500/10 border-red-500/30" },
    !lbStatus && spCreated && { label: "LawBillity", detail: "Pending sync", color: "text-txt-muted bg-navy-hover border-navy-border" },
    cclSigned && { label: "CCL Signed", detail: cclLabel, color: "text-teal bg-teal/10 border-teal/30" },
  ].filter(Boolean);

  const steps = [
    { key: "cc", label: "Conflict Check", status: ccStatus, sub: ccLabel, icon: Shield },
    { key: "ob", label: "Client Onboarding", status: obStatus, sub: obLabel, icon: UserPlus },
    { key: "aml", label: "AML / Thirdfort", status: tfStatus, sub: tfLabel, icon: Shield },
    { key: "forf", label: "FORF & Risk", status: forfStatus, sub: forfLabel, icon: Scale },
    { key: "ccl", label: "Client Care Letter", status: cclStatus, sub: cclLabel, icon: FileText },
    // 3 Nov 2026 (Vanessa): source of funds no longer gates the CCL; it is
    // done after it and gates Pre-Exchange approval, so it sits after the CCL.
    sofStep,
  ].filter(Boolean);

  const statusColor = (s) => s === "complete" ? "bg-teal" : s === "in_progress" ? "bg-status-amber" : "bg-navy-hover";
  const statusTextColor = (s) => s === "complete" ? "text-teal" : s === "in_progress" ? "text-status-amber" : "text-txt-faint";
  const statusIconColor = (s) => s === "complete" ? "text-teal" : s === "in_progress" ? "text-status-amber" : "text-txt-faint";
  const statusBg = (s) => s === "complete" ? "bg-teal/10" : s === "in_progress" ? "bg-status-amber/10" : "bg-navy-hover";
  const lineColor = (s) => s === "complete" ? "bg-teal" : "bg-navy-hover";

  return (
    <div className="rounded-md border border-navy-border bg-navy overflow-hidden mb-6" data-testid="golden-rule-progress">
      <div className="px-5 pt-4 pb-2 flex items-center gap-2">
        <Scale className="w-4 h-4 text-teal" />
        <span className="text-xs font-semibold text-teal tracking-widest uppercase">Golden Rule Progress</span>
      </div>

      <div className="px-5 pb-3">
        <div className="flex items-start">
          {steps.map((step, i) => (
            <div key={step.key} className="flex items-start flex-1 min-w-0">
              <div className="flex flex-col items-center flex-1">
                <div className="flex items-center w-full">
                  {i > 0 && <div className={`h-0.5 flex-1 ${lineColor(steps[i-1].status)} transition-colors duration-500`} />}
                  {i === 0 && <div className="flex-1" />}
                  <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${statusBg(step.status)} border-2 ${step.status === "complete" ? "border-teal" : step.status === "in_progress" ? "border-amber-500" : "border-navy-border"} transition-all duration-500`} data-testid={`gr-step-${step.key}`}>
                    {step.status === "complete" ? (
                      <CheckCircle2 className="w-4 h-4 text-teal" />
                    ) : (
                      <step.icon className={`w-3.5 h-3.5 ${statusIconColor(step.status)}`} />
                    )}
                  </div>
                  {i < steps.length - 1 && <div className={`h-0.5 flex-1 ${lineColor(step.status)} transition-colors duration-500`} />}
                  {i === steps.length - 1 && <div className="flex-1" />}
                </div>
                <span className={`text-[10px] font-semibold mt-1.5 text-center leading-tight ${step.status === "complete" ? "text-txt" : step.status === "in_progress" ? "text-status-amber" : "text-txt-muted"}`}>
                  {step.label}
                </span>
                <span className={`text-[9px] text-center leading-tight mt-0.5 ${statusTextColor(step.status)}`}>
                  {step.sub}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {milestones.length > 0 && (
        <div className="px-5 pb-4 flex flex-wrap gap-2 border-t border-navy-border pt-3">
          {milestones.map((m, i) => (
            <span key={i} className={`text-[10px] font-medium px-2.5 py-1 rounded-md border ${m.color}`} data-testid={`milestone-${i}`}>
              {m.label}: {m.detail}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MatterIntakeDetail({ submission, preview, formatDate, handleDownloadCCL, handleExportJson, handleCreateSharePointMatter, downloading, creatingSharePoint, navigate, isPreCaseMinder, isDirector, onDelete }) {
  const ownRef = ownDisplayRef(submission, preview) || "";
  // BM-H4109: the top bar Call note files to this matter while it is open.
  useEffect(() => {
    if (!ownRef) return undefined;
    setCurrentMatterRef(ownRef);
    return () => clearCurrentMatterRef(ownRef);
  }, [ownRef]);
  // Open CCL (Liezel, 22 Oct 2026): "Open CCL" lands here with ?tab=ccl, but
  // the CCL editor sits at the foot of a long page, so it looked as though the
  // button went nowhere. Take her to it. Cards above it load late and push it
  // down, so look again once they have; a click on the button while already
  // here is a new location key, so it scrolls again.
  const location = useLocation();
  const wantsCcl = new URLSearchParams(location.search).get("tab") === "ccl";
  useEffect(() => {
    if (!wantsCcl || !submission?.id) return undefined;
    const goToCcl = () => {
      const el = document.getElementById("ccl");
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    const timers = [150, 900, 2000].map((ms) => setTimeout(goToCcl, ms));
    // Once she scrolls herself, leave the page where she put it.
    const stop = () => timers.forEach(clearTimeout);
    window.addEventListener("wheel", stop, { once: true, passive: true });
    window.addEventListener("touchmove", stop, { once: true, passive: true });
    window.addEventListener("keydown", stop, { once: true });
    return () => {
      stop();
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchmove", stop);
      window.removeEventListener("keydown", stop);
    };
  }, [wantsCcl, submission?.id, location.key]);
  // PR feat/allocate-fee-earner-may (18 May 2026): widened from
  // director-only to also include May (cashier) — see the matching
  // backend change in hub/matters.py allocate_fee_earner endpoint.
  // The button + dialog visibility share this gate.
  //
  // BM-H126 (29 Jul 2026): also include Liezel (exec assistant). She
  // is the one asked to reallocate a fee earner after conflict
  // approval lands the matter on the wrong person. Mirrors the
  // EXEC_ASSISTANT_EMAILS list on the backend gate.
  //
  // ``useCurrentUser`` is the established auth hook already used at
  // the OB-skip site below (formerly line 692). Hoisted here so the
  // gate computed at top-of-component can share the same source —
  // avoids a redundant hook call and a duplicate identifier.
  const { email: currentUserEmail } = useCurrentUser();
  const normalisedCallerEmail = (currentUserEmail || "").toLowerCase().trim();
  const canAllocateFE = isDirector || ALLOCATE_FE_EXTRA_EMAILS.includes(normalisedCallerEmail);
  const [docxReviewed, setDocxReviewed] = useState(submission?.docx_reviewed || false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [fullPreview, setFullPreview] = useState(null);
  const [loadingFullPreview, setLoadingFullPreview] = useState(true);
  const [previewMode, setPreviewMode] = useState('full');
  const [confirmingReview, setConfirmingReview] = useState(false);
  // Brief 14e.2 — standalone SoF send (button visible regardless of CCL stage)
  const [sendingSofStandalone, setSendingSofStandalone] = useState(false);
  const [sofStandaloneSent, setSofStandaloneSent] = useState(false);

  const [signingToken, setSigningToken] = useState(submission?.signing_main_token || submission?.signing_token || null);
  const [signingStatus, setSigningStatus] = useState(submission?.signing_status || null);
  // BM-H474: the submission read is the source of truth for a client signature.
  // A remount after navigating back from the pipeline must pick it up, or the
  // CCL panel offers to send an already-signed engagement letter again.
  useEffect(() => {
    if (submission?.signing_status) setSigningStatus(submission.signing_status);
    if (submission?.signing_main_token) setSigningToken(submission.signing_main_token);
  }, [submission?.signing_status, submission?.signing_main_token]);
  // BM-H612: read-only here. The signature is applied on the one signature
  // screen, CclPostReviewActions inside the CCL editor.
  const feeEarnerSigApplied = submission?.fee_earner_signature_applied || false;
  const [clientEmail, setClientEmail] = useState(submission?.data?.client_email || submission?.data?.Q2 || "");

  const clientFirstName = (submission?.data?.Q1 || "").split(" ")[0];
  const clientSurname = (submission?.data?.Q1 || "").split(" ").slice(1).join(" ");

  const [creatingPortal, setCreatingPortal] = useState(false);
  const [portalTokens, setPortalTokens] = useState(null);
  const [portalSalutation, setPortalSalutation] = useState(clientFirstName);
  const [showPortalEmailEditor, setShowPortalEmailEditor] = useState(false);
  const defaultPortalEmailBody = "In order to proceed with your matter, we require some documents from you.\n\nPlease use the secure link below to upload the requested items.";
  const [portalEmailBody, setPortalEmailBody] = useState(defaultPortalEmailBody);
  const [portalJointEmail, setPortalJointEmail] = useState("");
  const [portalChecklist, setPortalChecklist] = useState([]);
  const [showPortalChecklist, setShowPortalChecklist] = useState(false);
  const [portalNewItem, setPortalNewItem] = useState("");

  // feat/allocate-fee-earner — director-only matter reallocation.
  // Backend: POST /api/matters/{matter_ref}/allocate-fee-earner.
  // Writes data.responsible_fee_earner + data.fee_earner_email so the
  // matters.py $ifNull chain (line 78-82) picks them up permanently.
  const [allocFeOpen, setAllocFeOpen] = useState(false);
  const [allocFeOptions, setAllocFeOptions] = useState([]);
  const [allocFeLoading, setAllocFeLoading] = useState(false);
  const [allocFeSelected, setAllocFeSelected] = useState("");
  const [allocFeSubmitting, setAllocFeSubmitting] = useState(false);

  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [savingField, setSavingField] = useState(false);

  const startFieldEdit = (field) => {
    const val = field === "Q3" ? (preview?.matter_title || submission?.data?.Q3 || "") : (ownDisplayRef(submission, preview) || "");
    setEditValue(val);
    setEditingField(field);
  };

  const cancelFieldEdit = () => { setEditingField(null); setEditValue(""); };

  const saveFieldEdit = async () => {
    if (!editingField || !editValue.trim()) return;
    setSavingField(true);
    try {
      const userId = sessionStorage.getItem("bonsai_user_id") || "";
      await axios.patch(`${API}/submissions/${submission.id}/field`, {
        field: editingField, value: editValue.trim(),
      }, { headers: { "X-Bonsai-User-Id": userId } });
      toast.success(editingField === "Q3" ? "Matter name updated" : "Matter reference updated");
      window.location.reload();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save"));
    } finally {
      setSavingField(false);
      setEditingField(null);
    }
  };

  const [sendingThirdfort, setSendingThirdfort] = useState(false);
  const [thirdfortPending, setThirdfortPending] = useState(submission?.thirdfort_pending || false);
  // FIX #161: thirdfortCompleted must NOT be seeded from submission?.thirdfort_completed.
  // That field lives on the submission document (wrong). The canonical source is
  // carePackGates.gates.thirdfort_complete.passed, synced via the useEffect below.
  const [thirdfortCompleted, setThirdfortCompleted] = useState(false);
  const [markingThirdfortComplete, setMarkingThirdfortComplete] = useState(false);
  const [regeneratingPdfs, setRegeneratingPdfs] = useState(false);

  const [carePackGates, setCarePackGates] = useState(null);
  // BM-H500 (Vanessa, via Jodie/Rebekah on Teams): the completed OB
  // form captures national_insurance_number / utr_or_vat / utr_vat_type /
  // companies_house_auth_code, but none of them were ever rendered on
  // this staff-facing submission review screen — fee earners had to open
  // the onboarding PDF pack (or another matter entirely) to find them.
  // Fetched from the same GET /onboarding/status/{ref} the SDLT link
  // panel below and ConflictsSearch.jsx already use.
  const [onboardingRecord, setOnboardingRecord] = useState(null);
  // pipelineStatus: single source of truth for the Golden Rule Progress
  // strip and for the "Conflict Check Required" panel gate. Fetched from
  // /pipeline/matter/{ref}/status which walks client_onboarding +
  // conflict_checks + matter_tracker. See backend/hub/pipeline.py
  // get_matter_pipeline_status (23May2026, BL.VEC.002.1096).
  const [pipelineStatus, setPipelineStatus] = useState(null);
  // BM-H3443: bumped when a manual CCL send is recorded, to re-read the status.
  const [cclRecordKey, setCclRecordKey] = useState(0);
  // BM-H3512: bumped when a director vouch clears AML, to re-read the strip.
  const [amlRecordKey, setAmlRecordKey] = useState(0);

  useEffect(() => {
    if (submission?.docx_reviewed) {
      setDocxReviewed(true);
      setHasDownloaded(true);
    }
  }, [submission?.docx_reviewed]);

  // BM-H500: load the onboarding record once a matter_reference resolves,
  // for the client tax-reference details block rendered below. Best
  // effort — a matter with no linked/completed onboarding yet simply
  // shows nothing, same tolerance as the other GET /onboarding/status/{ref}
  // consumers in this codebase.
  useEffect(() => {
    const matterRef = submission?.matter_reference || submission?.data?.Q4 || "";
    if (!matterRef) { setOnboardingRecord(null); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/onboarding/status/${encodeURIComponent(matterRef)}`);
        setOnboardingRecord(res.data);
      } catch {
        setOnboardingRecord(null);
      }
    })();
  }, [submission?.matter_reference, submission?.data?.Q4]);

  // BM-H4 — email-send preflight. Query the environment's send gate on load
  // so the Send-care-pack button reflects the truth BEFORE it's clicked: if
  // sending is off (or the mailbox is unreachable) we disable it and show a
  // red banner, instead of letting the click silently fail.
  const [emailStatus, setEmailStatus] = useState(null);
  // BM-H314: the preflight has THREE outcomes, not two. Absence of the red
  // banner used to mean either "the gate is open" or "we never found out",
  // and those were indistinguishable on screen. emailStatusState keeps them
  // apart: "unknown" until the read resolves, and "unreadable" if it failed.
  const [emailStatusState, setEmailStatusState] = useState("unknown");
  const [emailStatusError, setEmailStatusError] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/system/email-status`);
        if (!alive) return;
        if (res?.data) {
          setEmailStatus(res.data);
          setEmailStatusState("read");
        } else {
          setEmailStatus(null);
          setEmailStatusState("unreadable");
          setEmailStatusError("The email-status check answered with no body.");
        }
      } catch (err) {
        // Preflight failure is NOT an all-clear. We do not block the button on
        // a guess (the send itself refuses loud with a 409), but we say out
        // loud that the gate is unverified.
        console.error("email-status preflight failed:", err);
        if (!alive) return;
        setEmailStatus(null);
        setEmailStatusState("unreadable");
        setEmailStatusError(errorMessage(err, "The email gate could not be checked."));
      }
    })();
    return () => { alive = false; };
  }, []);

  // BM-H4 banners. BM-H501: the "the server just refused a send with a 409"
  // latch went with the retired Send care pack button; the send that remains
  // (Send to Client for Signing, in CclPostReviewActions) surfaces that 409 in
  // its own error toast. The health probe still drives both banners here.
  const emailSendBlocked =
    Boolean(emailStatus && (!emailStatus.emails_enabled || !emailStatus.mailbox_reachable));
  // True when we simply do not know. Never treated as healthy.
  const emailStatusUnverified = emailStatusState !== "read";

  // FIX #161 (cont): sync thirdfortCompleted exclusively from the gates response.
  // This fires once carePackGates loads and correctly reflects OB-side Thirdfort status.
  useEffect(() => {
    if (carePackGates?.gates?.thirdfort_complete?.passed === true) {
      setThirdfortCompleted(true);
    }
  }, [carePackGates]);

  const subData = submission?.data || {};
  const [ccSearchQuery, setCcSearchQuery] = useState(subData.Q1 || '');
  const [ccSearching, setCcSearching] = useState(false);
  const [ccResults, setCcResults] = useState(null);
  // ToB Schedule 1 director's guarantee (Sep 2026). Surfaced on the matter
  // so the guarantor's name and signature date are visible without opening
  // the OB file; the override is director/admin-only and demands a reason.
  const [dgOverrideOpen, setDgOverrideOpen] = useState(false);
  const [dgOverrideReason, setDgOverrideReason] = useState('');
  const [dgOverrideSaving, setDgOverrideSaving] = useState(false);
  const [ccDecision, setCcDecision] = useState('');
  const [ccNotes, setCcNotes] = useState('');
  const [ccSubmitting, setCcSubmitting] = useState(false);
  const [ccComplete, setCcComplete] = useState(!!submission?.conflict_check_id);
  // hasConflictCheck: union of every signal that says a cleared CC
  // exists for this matter. Includes pipelineStatus.pips.conflict_check.done,
  // which is true even when sub.conflict_check_id back-link is missing
  // (BL.VEC.002.1096 class — strip used to lie). Adding it here means the
  // red "Conflict Check Required" panel below ALSO stops rendering for
  // those matters — closing the path that allowed a duplicate CC to be
  // created with one tap of "Clear — No Conflict".
  const hasConflictCheck =
    !!submission?.conflict_check_id
    || ccComplete
    || carePackGates?.gates?.conflict_cleared?.passed
    || pipelineStatus?.pips?.conflict_check?.done === true
    || !!pipelineStatus?.conflict_check_id;

  // ---------------------------------------------------------------------------
  // Derived CCL context — used by CclEditor (single source of truth)
  // ---------------------------------------------------------------------------
  const cclMatterType = subData.Q9 || subData.sharepoint_matter_type || "";
  const cclFeeQuoted = subData.Q10 || subData.fee_quoted || preview?.fee_quoted || "";
  const cclClientName = subData.Q1 || "";
  // BM-H18: litigation / contentious matters never need a Source of Funds
  // questionnaire (no purchase, no client money movement, no AML SoF trigger).
  // Hide both the standalone SoF send button and the "Include SoF link" toggle
  // on the CCL send row when Q9 is 'disputes' so litigation fee earners don't
  // see two irrelevant SoF surfaces every time.
  const isDisputesMatter = (cclMatterType || "").toLowerCase().includes("dispute") ||
    (cclMatterType || "").toLowerCase().includes("litigation");

  const runConflictSearch = async () => {
    if (!ccSearchQuery.trim() || ccSearchQuery.trim().length < 2) return;
    setCcSearching(true);
    try {
      const res = await axios.get(`${API}/conflicts/search?query=${encodeURIComponent(ccSearchQuery.trim())}`);
      setCcResults(res.data);
    } catch (err) {
      setCcResults({ error: errorMessage(err, "Search failed"), results: [] });
    } finally {
      setCcSearching(false);
    }
  };

  const submitConflictCheck = async (status) => {
    setCcSubmitting(true);
    try {
      await axios.post(`${API}/conflict-checks`, {
        submission_id: submission?.id,
        matter_reference: subData.Q4 || submission?.matter_reference || '',
        client_name: subData.Q1 || '',
        client_type: 'individual',
        other_side_parties: [],
        hits_summary: ccResults?.results?.length ? `${ccResults.results.length} matches found` : 'No matches found',
        conflict_status: status,
        decision_maker_name: submission?.data?.Q6 || 'Fee Earner',
        decision_notes: ccNotes,
        search_query: ccSearchQuery
      });
      setCcComplete(true);
      const gatesRes = await axios.get(`${API}/submissions/${submission?.id}/care-pack-gates`);
      setCarePackGates(gatesRes.data);
    } catch (err) {
      alert('Failed to save conflict check: ' + errorMessage(err, "Failed to save conflict check"));
    } finally {
      setCcSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchGates = async () => {
      try {
        const res = await axios.get(`${API}/submissions/${submission?.id}/care-pack-gates`);
        setCarePackGates(res.data);
      } catch (err) {
        console.error("Failed to fetch care pack gates:", err);
      }
    };
    if (submission?.id) {
      fetchGates();
    }
  }, [submission?.id, docxReviewed, amlRecordKey]);

  // Cross-collection pipeline status (23May2026, BL.VEC.002.1096).
  // The Golden Rule Progress strip used to derive each stage from a
  // single submission doc — and lied when back-links were missing.
  // This effect fetches the canonical stage from
  // /pipeline/matter/{ref}/status, which walks every collection.
  // ccComplete is in the dep list so the strip refreshes immediately
  // after the in-page "Clear — No Conflict" panel writes a new CC.
  useEffect(() => {
    const ref =
      submission?.matter_reference
      || submission?.data?.Q4
      || submission?.data?.matter_reference
      || "";
    if (!ref) return;
    let cancelled = false;
    const fetchPipelineStatus = async () => {
      try {
        const res = await axios.get(
          `${API}/pipeline/matter/${encodeURIComponent(ref)}/status`
        );
        if (!cancelled) setPipelineStatus(res.data);
      } catch (err) {
        // 404 is benign — matter not yet in any collection. Other
        // errors fall back to legacy single-doc derivation in the
        // strip; no toast, no break.
        if (!cancelled && err?.response?.status !== 404) {
          console.warn("pipeline-status fetch failed:", err?.message || err);
        }
      }
    };
    fetchPipelineStatus();
    return () => { cancelled = true; };
  }, [submission?.id, submission?.matter_reference, ccComplete, docxReviewed, cclRecordKey, amlRecordKey]);

  useEffect(() => {
    const fetchFullPreview = async () => {
      try {
        setLoadingFullPreview(true);
        const response = await axios.get(`${API}/submissions/${submission?.id}/full-preview`);
        setFullPreview(response.data);
      } catch (error) {
        console.error("Failed to fetch full preview:", error);
      } finally {
        setLoadingFullPreview(false);
      }
    };
    if (submission?.id) {
      fetchFullPreview();
    }
  }, [submission?.id]);

  // Brief 14e.2 — fetch prior-send status to flip the button label
  useEffect(() => {
    const matterRef = submission?.matter_reference || submission?.data?.Q4 || "";
    if (!matterRef) return;
    let cancelled = false;
    (async () => {
      try {
        const userId = sessionStorage.getItem("bonsai_user_id") || "";
        const res = await fetch(
          `${API}/sof/send-standalone/${encodeURIComponent(matterRef)}/status`,
          { headers: { "X-Bonsai-User-Id": userId } },
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.sof_standalone_sent) setSofStandaloneSent(true);
      } catch (err) {
        // nosilent: non-fatal; button defaults to "Send"
        console.warn("sof-standalone status check failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, [submission?.matter_reference, submission?.data?.Q4]);

  // Shape a fetch error so utils/errorMessage reads the server payload
  // rather than falling through to "Couldn't reach the server" (BM-H529 —
  // handleSendSofStandalone used a raw fetch() and threw a bare
  // new Error(body.detail), which has no err.response, so errorMessage's
  // priority chain always misread it as a network error and hid the real
  // backend detail). Same helper and same reasoning as RefFixerPage's
  // buildFetchError / ClientFixerPage's buildFetchError.
  const buildFetchError = async (resp) => {
    let data = null;
    const text = await resp.text().catch(() => "");
    if (text) {
      try { data = JSON.parse(text); } catch (e) { data = text; }
    }
    const err = new Error(
      (data && typeof data === "object" && (data.detail?.message || data.detail || data.message || data.error))
      || (typeof data === "string" && data)
      || `${resp.status} ${resp.statusText}`
    );
    err.response = { status: resp.status, statusText: resp.statusText, data };
    return err;
  };

  // Brief 14e.2 — POST handler for the standalone SoF send
  const handleSendSofStandalone = async () => {
    const matterRef = submission?.matter_reference || submission?.data?.Q4 || "";
    if (!matterRef) {
      toast.error("Matter reference not available");
      return;
    }
    setSendingSofStandalone(true);
    try {
      const userId = sessionStorage.getItem("bonsai_user_id") || "";
      const res = await fetch(
        `${API}/sof/send-standalone/${encodeURIComponent(matterRef)}`,
        { method: "POST",
          headers: { "Content-Type": "application/json", "X-Bonsai-User-Id": userId } },
      );
      if (!res.ok) {
        throw await buildFetchError(res);
      }
      setSofStandaloneSent(true);
      toast.success("Source of Funds request sent");
    } catch (err) {
      toast.error(errorMessage(err, "Failed to send Source of Funds request"));
    } finally {
      setSendingSofStandalone(false);
    }
  };

  const handleDownloadWithTracking = async () => {
    await handleDownloadCCL();
    setHasDownloaded(true);
  };

  const handleConfirmReview = async () => {
    if (!hasDownloaded) {
      toast.error("Please download and review the CCL document first");
      return;
    }
    try {
      setConfirmingReview(true);
      await axios.post(`${API}/submissions/${submission?.id}/confirm-review`, null, {
        params: {
          reviewer_name: submission?.data?.submitter_name || "Unknown",
          reviewer_email: submission?.data?.submitter_email || ""
        }
      });
      setDocxReviewed(true);
      toast.success("DOCX review confirmed - CCL can now be sent to client");
    } catch (error) {
      console.error("Failed to confirm review:", error);
      toast.error("Failed to confirm review");
    } finally {
      setConfirmingReview(false);
    }
  };

  // BM-H612 (25 Sep 2026): handleMarkSent (POST /submissions/{id}/mark-sent,
  // which set care_pack_sent without sending anything and whose toast
  // claimed a draft had been created in the clientcare mailbox),
  // handleApplyFeeEarnerSignature, handleCreateSigningLink and
  // copySigningLink are GONE. None was wired to a button: the one
  // signature-and-send screen is CclPostReviewActions in the CCL editor.
  // Vanessa, 25 Sep: the clientcare@ draft is "extraneous now".

  // BM-H501 (11 Sep 2026): handleSendCarePack and the standalone "Send care
  // pack" button it fired are GONE. The welcome pack now travels with the
  // client care letter: pressing "Send to Client for Signing" (see
  // CclPostReviewActions) emails the signing link and the hosted welcome-pack
  // download link together. There is no second button to remember, and no
  // second, redundant send. The tripwire test
  // frontend/src/pages/detail/__tests__/MatterIntakeDetail.no-send-care-pack-button.test.jsx
  // fails if the button, its handler or the retired endpoint comes back.

  // feat/allocate-fee-earner — open the dialog + fetch the FE list.
  const handleOpenAllocateFeeEarner = async () => {
    setAllocFeSelected("");
    setAllocFeOpen(true);
    if (allocFeOptions.length === 0) {
      setAllocFeLoading(true);
      try {
        const res = await axios.get(`${API}/lookup/fee-earners`);
        // /api/lookup/fee-earners returns
        //   [{name, email, position}, ...]
        // already filtered to exclude former staff.
        const opts = Array.isArray(res?.data) ? res.data : [];
        setAllocFeOptions(opts);
      } catch (err) {
        toast.error(errorMessage(err, "Failed to load fee earner list"));
        setAllocFeOpen(false);
      } finally {
        setAllocFeLoading(false);
      }
    }
  };

  // feat/allocate-fee-earner — POST the chosen name to the backend.
  // On success: toast + page refresh so the new FE shows everywhere.
  const handleSubmitAllocateFeeEarner = async () => {
    const name = (allocFeSelected || "").trim();
    if (!name) {
      toast.error("Pick a fee earner from the list");
      return;
    }
    const matterRef = ownDisplayRef(submission, preview);
    if (!matterRef) {
      toast.error("Matter reference not found on this submission");
      return;
    }
    setAllocFeSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/matters/${encodeURIComponent(matterRef)}/allocate-fee-earner`,
        { fee_earner_name: name },
      );
      if (res?.data?.success) {
        const newFE = res.data.fee_earner?.name || name;
        const prev = res.data.previous_fee_earner || "";
        if (res.data.no_change) {
          toast.info(`Fee earner unchanged (already ${newFE})`);
        } else {
          toast.success(
            prev
              ? `Fee earner reallocated: ${prev} → ${newFE}`
              : `Fee earner allocated: ${newFE}`,
          );
        }
        setAllocFeOpen(false);
        // Refetch the matter detail so the banner / pipeline reflect
        // the new FE without a manual reload. Falling back to a full
        // navigate-replace keeps things working if the parent doesn't
        // wire a re-fetch yet.
        if (typeof window !== "undefined" && typeof window.location?.reload === "function") {
          window.location.reload();
        }
      } else {
        toast.error("Allocate Fee Earner returned an unexpected response");
      }
    } catch (err) {
      // Backend may return a structured 400 with valid_names — surface
      // that list to the operator instead of an opaque message.
      toast.error(errorMessage(err, "Allocate Fee Earner failed"));
    } finally {
      setAllocFeSubmitting(false);
    }
  };

  // Brief 85 — copy the hosted welcome-pack download URL so the fee earner
  // can share it manually if the client's mail server mangles the email.
  const copyPackDownloadLink = () => {
    const url = submission?.pack_download_url || "";
    if (!url) {
      toast.error("No pack download link available yet — send the letter for signing first");
      return;
    }
    navigator.clipboard.writeText(url);
    toast.success("Pack download link copied to clipboard");
  };

  const handleSendThirdfortReminder = async () => {
    try {
      setSendingThirdfort(true);
      const response = await axios.post(`${API}/submissions/${submission?.id}/thirdfort-reminder`);
      if (response.data.success) {
        toast.success("Thirdfort reminder sent to clientcare@bonsai.law");
        setThirdfortPending(true);
      } else if (response.data.already_sent_today) {
        toast.warning("Thirdfort reminder already sent today");
      } else {
        toast.error(response.data.message || "Failed to send reminder");
      }
    } catch (error) {
      console.error("Failed to send Thirdfort reminder:", error);
      toast.error(errorMessage(error, "Failed to send Thirdfort reminder"));
    } finally {
      setSendingThirdfort(false);
    }
  };

  // useCurrentUser() hoisted to top-of-component (line ~205) so the
  // canAllocateFE gate and this site share one source. See the comment
  // block at the top for context.

  const [creatingSPFolders, setCreatingSPFolders] = useState(false);
  const handleCreateSPFolders = async () => {
    setCreatingSPFolders(true);
    try {
      const res = await axios.post(`${API}/submissions/${submission?.id}/create-sp-folders`);
      const result = res.data?.result || {};
      const created = result.created?.length || 0;
      const skipped = result.skipped?.length || 0;
      if (created > 0) {
        toast.success(`Created ${created} folder(s)${skipped ? `, ${skipped} already existed` : ''}`);
      } else if (skipped > 0) {
        toast.info(`All ${skipped} folder(s) already exist`);
      } else {
        toast.success("Folder creation completed");
      }
    } catch (e) {
      toast.error(errorMessage(e, "Failed to create SP folders"));
    } finally {
      setCreatingSPFolders(false);
    }
  };

  const [showLinkOB, setShowLinkOB] = useState(false);
  const [linkOBValue, setLinkOBValue] = useState("");
  const [linkOBLoading, setLinkOBLoading] = useState(false);

  // BM-H3324: AML Policy §7.5, only a director (the MLCO or the MLRO) may skip
  // onboarding. This was a hard-coded list that included client care.
  const canSkipOB = !!isDirector;
  const [showSkipOB, setShowSkipOB] = useState(false);
  const [skipOBReason, setSkipOBReason] = useState("");
  const [skipOBLinkedRef, setSkipOBLinkedRef] = useState("");
  const [skipOBLoading, setSkipOBLoading] = useState(false);

  const [existingPreExChecklist, setExistingPreExChecklist] = useState(null);
  const [preExCheckLoading, setPreExCheckLoading] = useState(false);
  const [creatingPreExChecklist, setCreatingPreExChecklist] = useState(false);

  const handleSkipOB = async () => {
    if (!skipOBReason.trim() || !skipOBLinkedRef.trim()) return;
    setSkipOBLoading(true);
    try {
      await axios.post(`${API}/submissions/${submission?.id}/skip-ob`, {
        reason: skipOBReason.trim(),
        linked_matter_ref: skipOBLinkedRef.trim(),
        skipped_by: currentUserEmail.toLowerCase(),
      });
      toast.success("OB skipped — linked to existing matter");
      setShowSkipOB(false);
      setSkipOBReason("");
      setSkipOBLinkedRef("");
      window.location.reload();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to skip OB"));
    } finally {
      setSkipOBLoading(false);
    }
  };

  const handleLinkOB = async () => {
    if (!linkOBValue.trim()) return;
    setLinkOBLoading(true);
    try {
      const res = await axios.post(`${API}/submissions/${submission?.id}/link-onboarding`, {
        provisional_id: linkOBValue.trim()
      });
      toast.success(`Linked — OB: ${res.data.ob_status}, Thirdfort: ${res.data.thirdfort_status}`);
      setShowLinkOB(false);
      setLinkOBValue("");
      window.location.reload();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to link OB record"));
    } finally {
      setLinkOBLoading(false);
    }
  };

  const handleMarkThirdfortComplete = async () => {
    try {
      setMarkingThirdfortComplete(true);
      await axios.post(`${API}/submissions/${submission?.id}/thirdfort-complete`);
      toast.success("Thirdfort checks marked as complete");
      setThirdfortCompleted(true);
      setThirdfortPending(false);
    } catch (error) {
      console.error("Failed to mark Thirdfort complete:", error);
      toast.error("Failed to mark as complete");
    } finally {
      setMarkingThirdfortComplete(false);
    }
  };

  useEffect(() => {
    const fetchPortals = async () => {
      try {
        const res = await axios.get(`${API}/portal/submission/${submission?.id}/portals`);
        if (res.data?.length > 0) {
          setPortalTokens(res.data);
        }
      } catch { /* no portals yet */ }
    };
    if (submission?.id) fetchPortals();
  }, [submission?.id]);

  useEffect(() => {
    const matterRef = submission?.data?.Q4 || submission?.matter_reference || "";
    const matterType = submission?.data?.Q9 || submission?.data?.sharepoint_matter_type || "";
    const isResi = matterType.includes("resi") || matterType.includes("purchase") || matterType.includes("sale") || matterType.includes("remortgage") || matterType.includes("toe") || matterType.includes("auction") || !!refinanceMatterType(submission);
    if (!matterRef || !isResi) return;
    setPreExCheckLoading(true);
    // BM-H4191: a remortgage or transfer of equity has its own checklist.
    // A Pre-Exchange checklist made for one before this change is still
    // shown, so nothing already started disappears from the matter page.
    const isRefinance = !!refinanceMatterType(submission);
    const listOf = (checklistType) => axios.get(`${API}/checklists/list`, { params: { checklist_type: checklistType, matter_reference: matterRef } })
      .then(res => res.data || []);
    (isRefinance
      ? listOf("refinance_completion").then(found => (found.length ? found : listOf("pre_exchange")))
      : listOf("pre_exchange"))
      .then(found => {
        if (found.length > 0) setExistingPreExChecklist(found[0]);
      })
      .catch(() => { /* ignore */ })
      .finally(() => setPreExCheckLoading(false));
  }, [submission?.id]);

  const handleCreatePreExChecklist = async () => {
    const subData = submission?.data || {};
    const matterRef = subData.Q4 || submission?.matter_reference || "";
    const q9 = subData.Q9 || subData.sharepoint_matter_type || "";
    const refiType = refinanceMatterType(submission);
    if (refiType) return handleCreateRefinanceChecklist(subData, matterRef, refiType);
    const clientName = subData.Q1 || "";
    const feeEarner = subData.Q6 || preview?.fee_earner_name || "";
    const txMap = {
      purchase_resi: "purchase", sale_resi: "sale", remortgage: "purchase",
      remortgage_toe: "purchase", toe_only: "purchase", auction_review: "purchase",
    };
    const transactionType = txMap[q9] || "purchase";
    const isLeasehold = q9.includes("leasehold") || (subData.sharepoint_matter_type || "").toLowerCase().includes("leasehold");

    setCreatingPreExChecklist(true);
    try {
      const res = await axios.post(`${API}/checklists/create`, {
        checklist_type: "pre_exchange",
        matter_id: submission?.id || "",
        matter_reference: matterRef,
        client_name: clientName,
        fee_earner: feeEarner,
        transaction_type: transactionType,
        is_leasehold: isLeasehold,
      });
      setExistingPreExChecklist(res.data);
      toast.success("Pre-Exchange checklist created", {
        action: {
          label: "Open",
          onClick: () => window.open(`/pre-exchange-checklist?id=${encodeURIComponent(res.data.id)}`, "_blank"),
        },
      });
    } catch (e) {
      toast.error(errorMessage(e, "Failed to create checklist"));
    } finally {
      setCreatingPreExChecklist(false);
    }
  };

  // BM-H4191: remortgage / transfer of equity, individual or company. Before
  // this the button made a Pre-Exchange checklist mapped to "purchase".
  const handleCreateRefinanceChecklist = async (subData, matterRef, q9) => {
    const clientType = (subData.client_type || subData.entity_type || "").toLowerCase();
    const isLeasehold = q9.includes("leasehold") || (subData.sharepoint_matter_type || "").toLowerCase().includes("leasehold");
    setCreatingPreExChecklist(true);
    try {
      const res = await axios.post(`${API}/checklists/create`, {
        checklist_type: "refinance_completion",
        matter_id: submission?.id || "",
        matter_reference: matterRef,
        client_name: subData.Q1 || "",
        fee_earner: subData.Q6 || preview?.fee_earner_name || "",
        transaction_type: REFINANCE_TX_FROM_Q9[q9],
        forf_matter_type: q9,
        forf_submission_id: submission?.id || undefined,
        client_type: clientType,
        is_company: ["company", "llp"].includes(clientType),
        is_leasehold: isLeasehold,
      });
      setExistingPreExChecklist(res.data);
      toast.success("Remortgage / Transfer of Equity checklist created", {
        action: {
          label: "Open",
          onClick: () => window.open(`/refinance-completion-checklist?id=${encodeURIComponent(res.data.id)}`, "_blank"),
        },
      });
    } catch (e) {
      toast.error(errorMessage(e, "Failed to create checklist"));
    } finally {
      setCreatingPreExChecklist(false);
    }
  };

  const handleCreatePortalLink = async () => {
    if (!clientEmail.trim()) {
      toast.error("Client email is required");
      return;
    }
    setCreatingPortal(true);
    try {
      const subData = submission?.data || {};
      const matterType = subData.Q9 || subData.sharepoint_matter_type || "";
      // BM-H384: send the matter type and let the backend classify. This
      // substring guess could not see a private client at all, so a wills or
      // LPA matter came back false and the client was asked for a certificate
      // of incorporation. is_residential is still sent so an older backend
      // behaves as before, but where both arrive the backend's answer wins.
      const isResidential = matterType.includes("resi") || matterType.includes("purchase") || matterType.includes("sale");
      const res = await axios.post(`${API}/portal/create`, {
        matter_type: matterType,
        matter_description: subData.matter_description || subData.work_description || "",
        submission_id: submission?.id,
        matter_reference: subData.Q4 || submission?.matter_reference || "",
        client_name: subData.Q1 || "",
        client_email: clientEmail.trim(),
        fee_earner_name: subData.Q6 || preview?.fee_earner_name || "",
        fee_earner_email: subData.submitter_email || "",
        is_residential: isResidential,
        joint_client_email: portalJointEmail.trim() || null,
        joint_client_name: subData.Q1b || null,
        salutation: portalSalutation.trim() || null,
        email_body: portalEmailBody.trim() !== defaultPortalEmailBody.trim() ? portalEmailBody.trim() : null,
        checklist: portalChecklist.length > 0 ? portalChecklist : null,
      });
      if (res.data.success) {
        setPortalTokens(prev => {
          const arr = prev || [];
          return [...arr, { token: res.data.token, client_name: subData.Q1 || "", status: "active" }];
        });
        toast.success("Document portal created and emailed to client!");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Failed to create portal"));
    } finally {
      setCreatingPortal(false);
    }
  };

  const getStatusDisplay = () => {
    const status = submission?.status || "submitted";
    if (status === "sent" || status === "care_pack_prepared") {
      return { label: status === "care_pack_prepared" ? "Care Pack Prepared" : "Sent to Client", color: "bg-status-green-dim text-status-green border-green-300", icon: CheckCircle2 };
    } else if (status === "reviewed" || submission?.docx_reviewed) {
      return { label: "Reviewed - Ready to Send", color: "bg-blue-100 text-blue-800 border-blue-300", icon: FileCheck };
    } else {
      return { label: "Submitted", color: "bg-status-amber-dim/30 text-status-amber border-amber-500/40", icon: AlertTriangle };
    }
  };

  const statusDisplay = getStatusDisplay();
  const StatusIcon = statusDisplay.icon;

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* BM-H14 (2 Jul 2026): the old header crammed logo + status badges +
          h1 title + oversized ref badge + Ref line + a full row of action
          buttons into a rigid h-16 sticky nav. On any viewport narrower than
          huge it visually collapsed on top of itself (Vanessa's screenshot:
          FORF & Risk Assessment pill overlapping Matter Details title,
          BL.VEC.002.1142 mashed into the h1). Split into 3 layers:
            1. sticky slim nav — logo, status pills, Back
            2. matter identity block — title, big ref badge, Ref line
            3. action bar — buttons that wrap gracefully, with the
               email-disabled warning as a full-width strip above it
               (was a floating popover that z-fought the sticky nav). */}
      {/* Layer 1: sticky slim nav */}
      <nav className="sticky top-0 z-50 w-full glass-nav">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center min-h-16 py-2 gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <img src={LOGO_URL} alt="Bonsai Law" className="w-10 h-10 shrink-0" />
              <div className="flex flex-wrap items-center gap-2 min-w-0">
                <span className={`px-2 py-0.5 text-xs rounded-md flex items-center gap-1 whitespace-nowrap ${
                  isPreCaseMinder ? "bg-teal-dim text-teal border border-teal" : "bg-teal/10 text-teal border border-teal/30"
                }`}>
                  <Scale className="w-3 h-3" />
                  {isPreCaseMinder ? "Pre-CaseMinder" : "FORF & Risk Assessment"}
                </span>
                <span className={`px-2 py-0.5 text-xs rounded-md border flex items-center gap-1 whitespace-nowrap ${statusDisplay.color}`}>
                  <StatusIcon className="w-3 h-3" />
                  {statusDisplay.label}
                </span>
                {submission?.data?.is_test_submission && (
                  <span className="px-2 py-0.5 text-xs rounded-md bg-status-amber-dim text-status-amber border border-amber-300 flex items-center gap-1 whitespace-nowrap">
                    <FlaskConical className="w-3 h-3" />
                    TEST
                  </span>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="text-txt-muted hover:text-teal shrink-0"
              data-testid="matter-header-back-btn"
            >
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Back</span>
            </Button>
          </div>
        </div>
      </nav>

      {/*
        Layer 2: matter identity block.
        BM-H262: this band used to paint itself `bg-[#F5F5F0]`. Page roots
        carrying that same class are repainted transparent by the
        `.min-h-screen` rule in index.css, so the band was the only place
        on the page still rendering the old light surface, and it sat as a
        pale slab in the middle of the dark shell. The text inside it is
        styled with the slate scale, which index.css remaps to cream for
        dark backgrounds, so the reference line under the title came out
        cream on cream and read as cut off and half faded. The band now
        inherits the page surface like everything else and keeps only its
        divider.
      */}
      <div className="w-full border-b border-teal/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-teal flex items-center gap-2 flex-wrap" data-testid="matter-title">
              {editingField === "Q3" ? (
                <span className="flex items-center gap-1 flex-wrap">
                  <input
                    className="bg-white border border-teal/40 rounded px-2 py-1 text-teal text-lg font-bold w-64"
                    value={editValue} onChange={e => setEditValue(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") saveFieldEdit(); if (e.key === "Escape") cancelFieldEdit(); }}
                    autoFocus data-testid="edit-matter-name-input"
                  />
                  <Button size="sm" onClick={saveFieldEdit} disabled={savingField} className="h-7 px-2 bg-teal text-white" data-testid="save-matter-name-btn">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={cancelFieldEdit} className="h-7 px-2 text-slate-400" data-testid="cancel-matter-name-btn">
                    <XIcon className="w-3.5 h-3.5" />
                  </Button>
                </span>
              ) : (
                <>
                  <span className="break-words">{preview?.matter_title || "Matter Details"}</span>
                  <button onClick={() => startFieldEdit("Q3")} className="text-slate-500 hover:text-teal transition-colors shrink-0" title="Edit matter name" data-testid="edit-matter-name-btn">
                    <Pen className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </h1>
            {/* BM-817-REDUX: canonical matter_reference first; a poisoned
                preview.our_ref (a linked matter's ref) must never win. */}
            {(ownDisplayRef(submission, preview) || submission?.secondary_label) && (
              <div className="text-xl sm:text-2xl font-mono font-bold text-teal" data-testid="matter-reference-badge">
                {ownDisplayRef(submission, preview) || submission?.secondary_label}
              </div>
            )}
            <p className="text-xs text-slate-500 flex items-center gap-1.5 flex-wrap">
              Ref: {editingField === "Q4" ? (
                <span className="inline-flex items-center gap-1">
                  <input
                    className="bg-white border border-teal/40 rounded px-1.5 py-0.5 text-slate-700 text-xs w-40"
                    value={editValue} onChange={e => setEditValue(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") saveFieldEdit(); if (e.key === "Escape") cancelFieldEdit(); }}
                    autoFocus data-testid="edit-matter-ref-input"
                  />
                  <Button size="sm" onClick={saveFieldEdit} disabled={savingField} className="h-5 px-1.5 bg-teal text-white text-[10px]" data-testid="save-matter-ref-btn">
                    <CheckCircle2 className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={cancelFieldEdit} className="h-5 px-1.5 text-slate-400" data-testid="cancel-matter-ref-btn">
                    <XIcon className="w-3 h-3" />
                  </Button>
                </span>
              ) : (
                <>
                  {ownDisplayRef(submission, preview)}
                  <button onClick={() => startFieldEdit("Q4")} className="text-slate-500 hover:text-teal transition-colors" title="Edit matter reference" data-testid="edit-matter-ref-btn">
                    <Pen className="w-3 h-3" />
                  </button>
                </>
              )}
            </p>
            {/* BM-H3407: name every client on a joint matter, "Smith & Jones". */}
            {(submission?.client_display_name || subData.Q1) && (
              <p className="text-sm text-slate-600 mt-0.5" data-testid="matter-client-names">
                {(submission?.joint_client_names || []).length ? "Clients: " : "Client: "}
                <span className="font-medium text-slate-800">{submission?.client_display_name || subData.Q1}</span>
              </p>
            )}
          </div>

          {/* BM-H14: email-send disabled banner promoted to a full-width
              strip ABOVE the action row. Old placement was a small red pill
              floating next to Send that z-fought the sticky nav and
              overlapped adjacent controls. */}
          {emailSendBlocked && (
            <div
              className="mt-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 leading-snug flex items-start gap-2"
              data-testid="email-disabled-banner"
              role="alert"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <span className="font-semibold">Email sending is disabled.</span>{" "}
                Contact IT to enable EMAILS_ENABLED and Graph credentials. Draft-first path still works — Download Letter (.docx) is unaffected.
              </div>
            </div>
          )}

          {/* BM-H314: the third state. Amber, not red: we are not saying
              sending is off, we are saying we could not check. Silence here
              used to look identical to a healthy gate. */}
          {emailStatusUnverified && (
            <div
              className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 leading-snug flex items-start gap-2"
              data-testid="email-status-unknown-banner"
              role="status"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <span className="font-semibold">Email status could not be checked.</span>{" "}
                Email sending has NOT been verified for this environment, so a send may be refused. Directors can read the live gate at System Status in the avatar menu. Download Letter (.docx) is unaffected.
                {emailStatusError ? ` (${emailStatusError})` : ""}
              </div>
            </div>
          )}

          {/* Layer 3: action bar — wraps on narrower widths */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
              {(() => {
                const matterRefForEdit = ownDisplayRef(submission, preview);
                if (!matterRefForEdit) return null;
                return (
                  <Button
                    onClick={() => navigate(`/forf/${encodeURIComponent(matterRefForEdit)}`)}
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 gap-2"
                    data-testid="matter-edit-forf-btn"
                    title="Open the FORF page (revert to draft to edit fields)"
                  >
                    <Undo2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Edit FORF</span>
                  </Button>
                );
              })()}
              {/* BM-H4483 (19 Oct 2026): a Pre-CaseMinder registration opens for
                  time once its historic steps are recorded on the matter page
                  (BM-H4320), and nothing here led there. */}
              {isPreCaseMinder && (() => {
                const ref = ownDisplayRef(submission, preview);
                if (!ref) return null;
                return (
                  <Button
                    onClick={() => navigate(`/matter/${encodeURIComponent(ref)}#pre-cm-history`)}
                    className="bg-teal text-white gap-2"
                    data-testid="pre-cm-record-steps-btn"
                    title="Record when each golden rule step was done before CaseMinder. Time opens once the client care letter step is on record."
                  >
                    <History className="w-4 h-4" />
                    <span>Record steps done before CaseMinder</span>
                  </Button>
                );
              })()}
              {/* feat/allocate-fee-earner — widened from director-only to
                  also include May (cashier) so she can unblock approval
                  flows on legacy matters without escalating to a director.
                  See ``canAllocateFE`` at the top of this component and
                  the matching hub/matters.py authz change. */}
              {canAllocateFE && (
                <Button
                  onClick={handleOpenAllocateFeeEarner}
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 gap-2"
                  data-testid="matter-allocate-fee-earner-btn"
                  title="Assign or reassign the responsible fee earner (directors, May and Liezel only)"
                >
                  <UserPlus className="w-4 h-4" />
                  <span className="hidden sm:inline">Allocate Fee Earner</span>
                </Button>
              )}
              <Button
                onClick={handleDownloadWithTracking}
                disabled={downloading}
                className="bg-teal hover:bg-teal-hover text-white gap-2"
                data-testid="download-ccl-btn"
              >
                {downloading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">Download Letter (.docx)</span>
                  </>
                )}
              </Button>
              {/* Brief 85 / BM-H501 — the hosted welcome-pack download link,
                  written by the signing send, so a fee earner can share it by
                  hand if a client's mail server mangles the email. */}
              {submission?.pack_download_url && (
                <button
                  type="button"
                  onClick={copyPackDownloadLink}
                  className="text-[11px] text-emerald-700 hover:underline text-left leading-tight"
                  data-testid="copy-pack-download-link-btn"
                >
                  Pack download link: copy link
                </button>
              )}
              {/* Brief 14e.2 — standalone SoF questionnaire send.
                  BM-H18: hidden entirely on litigation matters — no purchase,
                  no client money movement, no AML SoF trigger. */}
              {!isDisputesMatter && (
                <Button
                  onClick={handleSendSofStandalone}
                  disabled={sendingSofStandalone}
                  variant="outline"
                  className="border-emerald-700 text-emerald-700 hover:bg-emerald-50 gap-2"
                  data-testid="send-sof-standalone-btn"
                  title="Send a Source of Funds questionnaire request directly to the client"
                >
                  {sendingSofStandalone ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /><span className="hidden sm:inline">Sending...</span></>
                  ) : sofStandaloneSent ? (
                    <span className="hidden sm:inline">Resend Source of Funds Request</span>
                  ) : (
                    <span className="hidden sm:inline">Send Source of Funds Request</span>
                  )}
                </Button>
              )}
              {/* Brief 69 — overflow: rare + destructive actions tucked
                  behind a kebab so the top bar isn't a wall of equal-weight
                  buttons. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-teal/30 text-teal hover:bg-teal hover:text-white px-2"
                    data-testid="matter-overflow-menu-btn"
                    title="More actions"
                    aria-label="More actions"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem
                    onClick={() => window.dispatchEvent(new CustomEvent('open-call-note', { detail: { matterRef: ownDisplayRef(submission, preview) } }))}
                    data-testid="matter-call-note-btn"
                    className="gap-2 cursor-pointer"
                  >
                    <Phone className="w-4 h-4" /> Call Note
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleExportJson}
                    data-testid="export-json-btn"
                    className="gap-2 cursor-pointer"
                  >
                    <FileJson className="w-4 h-4" /> Export JSON
                  </DropdownMenuItem>
                  {/* #886 (Liezel, 16 Jul 2026): when a matter has no
                      SharePoint folder yet (e.g. the 07 IP folder failed to
                      auto-provision), give a manual re-stage path. Previously
                      handleCreateSPFolders existed but was never rendered, so
                      there was no way to stage IP matters from this page. */}
                  {!submission?.sharepoint_created && (
                    <DropdownMenuItem
                      onClick={handleCreateSPFolders}
                      disabled={creatingSPFolders}
                      data-testid="stage-sharepoint-btn"
                      className="gap-2 cursor-pointer"
                    >
                      {creatingSPFolders
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Cloud className="w-4 h-4" />}
                      Save and stage to SharePoint
                    </DropdownMenuItem>
                  )}
                  {isDirector && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={onDelete}
                        data-testid="delete-submission-btn"
                        className="gap-2 cursor-pointer text-status-red focus:text-status-red focus:bg-status-red-dim"
                      >
                        <Trash2 className="w-4 h-4" /> Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {submission?.data?.is_test_submission && (
          <div className="mb-6 p-4 bg-status-amber-dim/30 border border-amber-500/40 rounded-md flex items-center gap-3">
            <FlaskConical className="w-5 h-5 text-status-amber" />
            <div>
              <p className="font-medium text-status-amber">Test Submission</p>
              <p className="text-sm text-status-amber/80">This is a pilot test submission and can be deleted later.</p>
            </div>
          </div>
        )}

        {/* BM-H3350: one line saying what to do next, or why it stopped. */}
        {pipelineStatus?.matter_reference && (
          <NextStepBanner matterRef={pipelineStatus.matter_reference} status={pipelineStatus} />
        )}

        {/* BM-H3681: the MLCO's decisions on this matter, for the MLCO and COLP only. */}
        {pipelineStatus?.matter_reference && (
          <MatterMLCOPanel matterRef={pipelineStatus.matter_reference} />
        )}

        {/* BM-H3532: a deferred conflict check stays visible until it is run. */}
        <DeferredConflictBanner deferred={pipelineStatus?.conflict_deferred} />

        <GoldenRuleProgress
          submission={submission}
          carePackGates={carePackGates}
          signingStatus={signingStatus}
          feeEarnerSigApplied={feeEarnerSigApplied}
          docxReviewed={docxReviewed}
          pipelineStatus={pipelineStatus}
        />

        {/* Cross-collection inconsistency banner (23May2026, BL.VEC.002.1096).
            When the pipeline-status endpoint reports a healable inconsistency
            (e.g. cleared CC but missing OB.conflict_check_id back-link), show
            it here so a director / admin can heal it instead of creating a
            duplicate CC by tapping "Clear — No Conflict". */}
        {pipelineStatus?.inconsistencies?.length > 0 && (
          <div className="mb-4 rounded-md border border-amber-500/40 bg-status-amber-dim/20 p-3" data-testid="pipeline-inconsistency-banner">
            <p className="text-xs font-bold text-status-amber mb-1">Cross-collection inconsistency detected</p>
            <ul className="text-xs text-status-amber/90 list-disc pl-5 space-y-1">
              {pipelineStatus.inconsistencies.map((iss, idx) => (
                <li key={idx}>
                  <span className="font-semibold">{iss.code}:</span> {iss.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* BM-H482d, 10 Sep 2026. The Client Care Letter and the Terms of
            Business are two separate agreements taken at two separate points:
            the terms at onboarding, the letter at the far end. A matter could
            reach the letter stage with the terms never agreed and nothing
            anywhere said so. This is deliberately not a gate: it does not
            disable the send, because a firm-side omission must not leave a
            client waiting on their letter. It is loud to us and invisible to
            them, on the screen the fee earner already reads before sending. */}
        {carePackGates?.gates?.terms_of_business_agreed
          && carePackGates.gates.terms_of_business_agreed.passed === false
          && carePackGates.gates.terms_of_business_agreed.at_letter_stage && (
          <div className="mb-4" data-testid="terms-not-agreed-panel">
            <div className="bg-navy border border-status-amber/40 rounded-md p-3 space-y-1">
              <p className="text-xs font-medium text-status-amber">
                Terms of Business not agreed. This matter is at the Client Care Letter stage.
              </p>
              <p className="text-xs text-txt-muted">
                {carePackGates.gates.terms_of_business_agreed.detail === 'requested_not_returned'
                  ? "The client was sent the Terms of Business and has not come back. Chase it before the letter goes out."
                  : carePackGates.gates.terms_of_business_agreed.detail === 'incomplete_record'
                  ? "The acceptance on file is half written: the tick is there but no date of acceptance. Check it before the letter goes out."
                  : carePackGates.gates.terms_of_business_agreed.detail === 'no_onboarding_record'
                  ? "There is no onboarding record for this matter, so no Terms of Business acceptance can be found. Check it before the letter goes out."
                  : "The client has not accepted the firm's Terms of Business. Check it before the letter goes out."}
              </p>
              <p className="text-[11px] text-txt-muted">
                The letter can still be sent. This is a warning to the firm, not a block on the client.
              </p>
            </div>
          </div>
        )}

        {carePackGates?.gates?.director_guarantee?.required && (
          <div className="mb-4" data-testid="director-guarantee-panel">
            {carePackGates.gates.director_guarantee.passed ? (
              <div className="bg-navy border border-status-green/40 rounded-md p-3">
                <p className="text-xs font-medium text-status-green mb-1">Director's Guarantee — recorded</p>
                {carePackGates.gates.director_guarantee.detail === 'overridden' ? (
                  <p className="text-xs text-txt-muted">
                    Overridden by {carePackGates.gates.director_guarantee.overridden_by || 'unknown'} —
                    {' '}{carePackGates.gates.director_guarantee.override_reason}
                  </p>
                ) : (
                  <p className="text-xs text-txt-muted" data-testid="director-guarantee-signed">
                    Guarantor: <span className="text-white">{carePackGates.gates.director_guarantee.guarantor_name}</span>
                    {carePackGates.gates.director_guarantee.guarantor_position
                      ? ` (${carePackGates.gates.director_guarantee.guarantor_position})` : ''}
                    {' '}· signed {formatDate ? formatDate(carePackGates.gates.director_guarantee.signed_at)
                      : carePackGates.gates.director_guarantee.signed_at}
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-navy border border-status-amber/40 rounded-md p-3 space-y-2">
                <p className="text-xs font-medium text-status-amber">
                  Director's Guarantee outstanding — corporate client. The CCL cannot be
                  marked signed until the guarantee is signed personally or overridden.
                </p>
                {isDirector && (!dgOverrideOpen ? (
                  <button
                    onClick={() => setDgOverrideOpen(true)}
                    className="text-xs text-txt-muted hover:text-teal"
                    data-testid="dg-override-toggle"
                  >
                    Record an override
                  </button>
                ) : (
                  <div className="space-y-2">
                    <textarea
                      value={dgOverrideReason}
                      onChange={e => setDgOverrideReason(e.target.value)}
                      placeholder="Written reason for proceeding without a signed guarantee"
                      className="w-full bg-navy-hover border border-navy-border rounded px-3 py-1.5 text-white text-xs"
                      data-testid="dg-override-reason"
                    />
                    <div className="flex gap-2">
                      <button
                        disabled={dgOverrideSaving || !dgOverrideReason.trim()}
                        data-testid="dg-override-confirm"
                        className="bg-status-amber disabled:opacity-50 text-white text-xs px-3 py-1.5 rounded"
                        onClick={async () => {
                          const ref = submission?.matter_reference || submission?.data?.Q4 || '';
                          setDgOverrideSaving(true);
                          try {
                            await axios.post(
                              `${API}/onboarding/${encodeURIComponent(ref)}/director-guarantee-override`,
                              { reason: dgOverrideReason.trim() },
                            );
                            const gatesRes = await axios.get(`${API}/submissions/${submission?.id}/care-pack-gates`);
                            setCarePackGates(gatesRes.data);
                            setDgOverrideOpen(false);
                            setDgOverrideReason('');
                          } catch (err) {
                            const detail = err.response?.data?.detail;
                            if (typeof window !== 'undefined' && typeof window.errorMessage === 'function') {
                              window.errorMessage(typeof detail === 'string' ? detail : 'Override failed');
                            }
                          } finally {
                            setDgOverrideSaving(false);
                          }
                        }}
                      >
                        {dgOverrideSaving ? 'Saving...' : 'Confirm override'}
                      </button>
                      <button
                        onClick={() => { setDgOverrideOpen(false); setDgOverrideReason(''); }}
                        className="text-txt-muted hover:text-white text-xs px-2"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!carePackGates?.gates?.onboarding_complete?.passed && (
          <div className="mb-4">
            {!showLinkOB ? (
              <button
                onClick={() => setShowLinkOB(true)}
                className="text-xs text-txt-muted hover:text-teal flex items-center gap-1 transition-colors"
                data-testid="link-ob-toggle"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                Link existing OB record
              </button>
            ) : (
              <div className="bg-navy border border-amber-500/40 rounded-md p-4 space-y-3" data-testid="link-ob-panel">
                <p className="text-xs font-medium text-status-amber">Link Onboarding Record</p>
                <p className="text-xs text-txt-muted">Enter the onboarding reference (e.g. <span className="font-mono">OB-2026-0075</span> for a pre-CaseMinder OB, or the <span className="font-mono">BL.XXX.YYY.NNNN</span> matter reference) to link a completed onboarding record to this matter.</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={linkOBValue}
                    onChange={e => setLinkOBValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleLinkOB()}
                    placeholder="OB-2026-0075"
                    className="flex-1 bg-navy-hover border border-navy-border rounded px-3 py-1.5 text-white text-xs font-mono focus:outline-none focus:border-amber-500"
                    data-testid="link-ob-input"
                  />
                  <button
                    onClick={handleLinkOB}
                    disabled={linkOBLoading || !linkOBValue.trim()}
                    className="bg-status-amber hover:bg-status-amber disabled:opacity-50 text-white text-xs px-3 py-1.5 rounded"
                    data-testid="link-ob-confirm"
                  >
                    {linkOBLoading ? 'Linking...' : 'Link'}
                  </button>
                  <button
                    onClick={() => { setShowLinkOB(false); setLinkOBValue(''); }}
                    className="text-txt-muted hover:text-white text-xs px-2"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* BM-H3443 (Vanessa, 2 Oct 2026): record a CCL sent by hand and saved
            into the matter, so the pipeline knows it went. */}
        {pipelineStatus?.matter_reference && (
          <section className="mb-4 rounded-lg overflow-hidden border border-navy-border"
                   data-testid="intake-ccl-sent-manually">
            <h2 className="text-sm font-semibold text-white bg-navy-hover px-4 py-2">
              Client care letter sent outside CaseMinder
            </h2>
            <CclSentOffSystem matterRef={pipelineStatus.matter_reference}
                              onChange={() => setCclRecordKey((k) => k + 1)} />
          </section>
        )}

        {/* BM-H3512 (walk-through 3 Oct 2026): the director vouch for client
            ID (BM-H3306) was only on /matter/:ref, not on this card, which is
            the one staff use. Same component and endpoint: it shows only to
            directors (and the server checks again), only while AML is not
            satisfied, and says who vouched once they have. */}
        {pipelineStatus?.matter_reference && (
          <DirectorVouchPanel
            matterRef={pipelineStatus.matter_reference}
            onAmlSatisfied={() => setAmlRecordKey((k) => k + 1)}
          />
        )}

        {/* BM-H3355: AML Policy §7.14, report money received before CDD to the MLRO. */}
        <div className="mb-4">
          <ReportFundsBeforeCDD matterRef={submission?.matter_reference || submission?.data?.Q4 || ""} />
        </div>
        {(carePackGates === null || !carePackGates?.gates?.onboarding_complete?.passed) && canSkipOB && (
          <div className="mb-4">
            {!showSkipOB ? (
              <button
                onClick={() => setShowSkipOB(true)}
                className="text-xs text-status-amber/70 hover:text-status-amber flex items-center gap-1 transition-colors"
                data-testid="skip-ob-toggle"
              >
                <AlertTriangle className="w-3 h-3" />
                Skip OB — linked to existing matter
              </button>
            ) : (
              <div className="bg-navy border border-amber-500/40 rounded-md p-4 space-y-3" data-testid="skip-ob-panel">
                <p className="text-xs font-medium text-status-amber">Skip OB (Linked Matter)</p>
                <p className="text-xs text-txt-muted">Skip client onboarding for this matter because it is linked to an existing matter where OB is already complete. This action is recorded for audit purposes.</p>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={skipOBLinkedRef}
                    onChange={e => setSkipOBLinkedRef(e.target.value)}
                    placeholder="Linked matter reference (e.g. BL.2026.0042)"
                    className="w-full bg-navy-hover border border-navy-border rounded px-3 py-1.5 text-white text-xs font-mono focus:outline-none focus:border-amber-500"
                    data-testid="skip-ob-ref"
                  />
                  <input
                    type="text"
                    value={skipOBReason}
                    onChange={e => setSkipOBReason(e.target.value)}
                    placeholder="Reason (e.g. same client, related transaction)"
                    className="w-full bg-navy-hover border border-navy-border rounded px-3 py-1.5 text-white text-xs focus:outline-none focus:border-amber-500"
                    data-testid="skip-ob-reason"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSkipOB}
                    disabled={skipOBLoading || !skipOBReason.trim() || !skipOBLinkedRef.trim()}
                    className="bg-status-amber hover:bg-status-amber disabled:opacity-50 text-white text-xs px-3 py-1.5 rounded"
                    data-testid="skip-ob-confirm"
                  >
                    {skipOBLoading ? 'Skipping...' : 'Skip OB'}
                  </button>
                  <button
                    onClick={() => { setShowSkipOB(false); setSkipOBReason(''); setSkipOBLinkedRef(''); }}
                    className="text-txt-muted hover:text-white text-xs px-2"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {(() => {
          const matterType = submission?.data?.Q9 || submission?.data?.sharepoint_matter_type || "";
          const isResi = matterType.includes("resi") || matterType.includes("purchase") || matterType.includes("sale") || matterType.includes("remortgage") || matterType.includes("toe") || matterType.includes("auction") || !!refinanceMatterType(submission);
          const matterRef = submission?.data?.Q4 || submission?.matter_reference || "";
          if (!isResi || !matterRef) return null;
          if (preExCheckLoading) return null;
          const isRefinance = !!refinanceMatterType(submission);
          if (existingPreExChecklist && existingPreExChecklist.checklist_type === "refinance_completion") {
            return (
              <div className="mb-4">
                <a
                  href={`/refinance-completion-checklist?id=${encodeURIComponent(existingPreExChecklist.id)}`}
                  className="text-xs text-teal hover:text-teal flex items-center gap-1 transition-colors"
                  data-testid="existing-refinance-checklist-link"
                >
                  <FileCheck className="w-3 h-3" />
                  Remortgage / Transfer of Equity checklist exists: open
                  <ChevronRight className="w-3 h-3" />
                </a>
              </div>
            );
          }
          if (existingPreExChecklist) {
            return (
              <div className="mb-4">
                <a
                  href={`/pre-exchange-checklist?id=${encodeURIComponent(existingPreExChecklist.id)}`}
                  className="text-xs text-teal hover:text-teal flex items-center gap-1 transition-colors"
                  data-testid="existing-pre-exchange-link"
                >
                  <FileCheck className="w-3 h-3" />
                  Pre-Exchange checklist exists — open
                  <ChevronRight className="w-3 h-3" />
                </a>
              </div>
            );
          }
          return (
            <div className="mb-4">
              <button
                onClick={handleCreatePreExChecklist}
                disabled={creatingPreExChecklist}
                className="text-xs text-txt-muted hover:text-teal flex items-center gap-1 transition-colors disabled:opacity-50"
                data-testid="create-pre-exchange-btn"
              >
                {creatingPreExChecklist ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                {creatingPreExChecklist ? "Creating..." : (isRefinance ? "Create Remortgage / Transfer of Equity Checklist" : "Create Pre-Exchange Checklist")}
              </button>
            </div>
          );
        })()}

        {(() => {
          const matterRefForComms = ownDisplayRef(submission, preview);
          if (!matterRefForComms) return null;
          return (
            <div className="mb-4">
              <CommunicationsPanel matterReference={matterRefForComms} />
            </div>
          );
        })()}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* BM-H4541: an older matter registered here keeps its number. A
                new conflict check from this box was refused (it carries the
                reference) and sent staff to the conflicts screen, which opens a
                new matter number and folder (BL.HD.002.1569, 20 Oct 2026). */}
            {!hasConflictCheck && isPreCaseMinder && ownRef && (
              <div className="rounded-md border-2 border-teal/50 bg-teal-dim/20 shadow-sm p-4" data-testid="pre-cm-conflict-pointer">
                <div className="flex items-center gap-2 mb-2">
                  <History className="w-5 h-5 text-teal" />
                  <h3 className="font-bold text-teal">Conflict check done before CaseMinder</h3>
                </div>
                <p className="text-xs text-txt-secondary mb-3">
                  Record when it was done, with the other golden rule steps, on the matter page. CaseMinder looks in
                  the matter&apos;s folder for the documents. Do not start a new conflict check: that opens a new matter number and folder.
                </p>
                <Button
                  onClick={() => navigate(`/matter/${encodeURIComponent(ownRef)}#pre-cm-history`)}
                  className="bg-teal text-white gap-2 text-sm"
                  data-testid="pre-cm-conflict-pointer-btn"
                >
                  <History className="w-4 h-4" /> Record steps done before CaseMinder
                </Button>
              </div>
            )}
            {!hasConflictCheck && !isPreCaseMinder && (
              <div className="rounded-md border-2 border-red-400/60 bg-status-red-dim/30 shadow-sm overflow-hidden" data-testid="inline-conflict-check">
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-5 h-5 text-status-red" />
                    <h3 className="font-bold text-status-red">Conflict Check Required</h3>
                  </div>
                  <p className="text-xs text-status-red/80 mb-3">Search the conflicts index before this matter can proceed.</p>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={ccSearchQuery}
                      onChange={(e) => setCcSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && runConflictSearch()}
                      placeholder="Client name or party name..."
                      className="flex-1 px-3 py-2 text-sm border border-red-400/40 rounded-md bg-[rgba(26,37,71,0.9)] text-white placeholder:text-txt-muted focus:outline-none focus:ring-2 focus:ring-red-400"
                      data-testid="cc-search-input"
                    />
                    <Button
                      onClick={runConflictSearch}
                      disabled={ccSearching || ccSearchQuery.trim().length < 2}
                      className="bg-status-red hover:bg-status-red text-white text-sm px-4"
                      data-testid="cc-search-btn"
                    >
                      {ccSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                  </div>
                  {ccResults && (
                    <div className="space-y-2">
                      {ccResults.error ? (
                        <p className="text-xs text-status-red">{ccResults.error}</p>
                      ) : ccResults.results?.length === 0 ? (
                        <div className="bg-teal-dim/30 border border-teal/40 rounded p-3">
                          <p className="text-sm text-teal font-medium">No conflicts found for "{ccSearchQuery}"</p>
                        </div>
                      ) : (
                        <div className="bg-[rgba(26,37,71,0.9)] border border-red-400/40 rounded max-h-48 overflow-y-auto">
                          <div className="p-2 bg-status-amber-dim/30 border-b border-amber-500/40">
                            <p className="text-xs font-medium text-status-amber">{ccResults.results.length} potential match(es) found</p>
                          </div>
                          {ccResults.results.map((r, i) => (
                            <div key={i} className="p-2 border-b border-navy-border last:border-0 text-xs">
                              <div className="font-medium text-white">{r.name || r.title}</div>
                              <div className="text-slate-500">{r.matter_reference} &mdash; {r.matter_title}</div>
                              <div className="text-slate-400">{r.entity_type}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="pt-2 space-y-2">
                        <textarea
                          value={ccNotes}
                          onChange={(e) => setCcNotes(e.target.value)}
                          placeholder="Decision notes (optional)..."
                          rows={2}
                          className="w-full px-3 py-2 text-sm border border-navy-border rounded-md focus:outline-none focus:ring-2 focus:ring-teal"
                          data-testid="cc-notes"
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={() => submitConflictCheck('clear')}
                            disabled={ccSubmitting}
                            className="flex-1 bg-status-green hover:bg-status-green text-white text-sm"
                            data-testid="cc-clear-btn"
                          >
                            {ccSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                            Clear &mdash; No Conflict
                          </Button>
                          <Button
                            onClick={() => submitConflictCheck('conflict_found')}
                            disabled={ccSubmitting}
                            variant="outline"
                            className="flex-1 border-amber-400 text-status-amber hover:bg-status-amber-dim text-sm"
                            data-testid="cc-flag-btn"
                          >
                            <AlertTriangle className="w-4 h-4 mr-1" />
                            Flag for Review
                          </Button>
                        </div>
                        <p className="text-[10px] text-slate-400">
                          {(subData.Q4 || submission?.matter_reference)
                            ? `PDF will be filed to ${subData.Q4 || submission?.matter_reference}/Compliance`
                            : 'Provide a matter reference above for auto-filing to compliance folder'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {ccComplete && !submission?.conflict_check_id && (
              <div className="rounded-md border-2 border-teal/40 bg-teal-dim/20 shadow-sm p-4" data-testid="cc-complete-banner">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-status-green" />
                  <span className="font-bold text-status-green text-sm">Conflict Check Completed</span>
                </div>
                <p className="text-xs text-status-green mt-1">Check has been recorded and filed to compliance.</p>
              </div>
            )}

            {/* Review Status & Actions — remainder of component unchanged from caseminder-cal main */}
          </div>
        </div>

        {/* Brief 15 Fire 1: mount CclEditor so fee earners can access
            the Stage 2 editor from the matter detail page. Props
            sourced from derived CCL context block above (cclMatterType,
            cclFeeQuoted, cclClientName). Gated on submission?.id so it
            renders only when the submission document is loaded. */}
        {submission?.id && (
          <div id="ccl" className="mt-8 scroll-mt-4" data-testid="ccl-editor-section">
            <CclEditor
              submissionId={submission.id}
              submissionData={submission.data}
              matterType={cclMatterType}
              feeQuoted={cclFeeQuoted}
              clientName={cclClientName}
              initialReviewed={docxReviewed}
              onReviewConfirmed={() => setDocxReviewed(true)}
              carePackSent={Boolean(submission?.care_pack_sent)}
              onReverted={() => { setDocxReviewed(false); setSigningStatus(null); setSigningToken(null); }}
              signingStatus={signingStatus}
              signingToken={signingToken}
            />
          </div>
        )}

        {/* BM-H500: the completed OB form captures the client's National
            Insurance number, UTR/VAT number, and (new) Companies House
            auth code, but none of them were visible on this staff review
            screen — Jodie/Rebekah had to open the onboarding PDF pack to
            find them when preparing an SDLT return. Rendered in the same
            style as the individual/corporate detail rows this page
            otherwise doesn't surface, conditionally on a truthy value
            (same convention as the PDF-pack rendering in
            onboarding_docs.py). */}
        {onboardingRecord && (() => {
          const primary = onboardingRecord.primary_client || {};
          const isCorporate = ["company", "llp"].includes(onboardingRecord.entity_type);
          const niNumber = primary.national_insurance_number || "";
          const utrOrVat = primary.utr_or_vat || "";
          const utrVatType = primary.utr_vat_type || "VAT";
          const cohoCode = primary.companies_house_auth_code || "";
          if (!niNumber && !utrOrVat && !cohoCode) return null;
          return (
            <div className="mt-8 rounded-md border border-navy-border bg-white/5 p-4" data-testid="client-tax-reference-details">
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2">Tax Reference Details</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                {niNumber && (
                  <div data-testid="detail-ni-number">
                    <div className="text-txt-muted text-xs">National Insurance Number</div>
                    <div className="text-white font-mono">{niNumber}</div>
                  </div>
                )}
                {utrOrVat && (
                  <div data-testid="detail-utr-vat">
                    <div className="text-txt-muted text-xs">{utrVatType} Number</div>
                    <div className="text-white font-mono">{utrOrVat}</div>
                  </div>
                )}
                {isCorporate && cohoCode && (
                  <div data-testid="detail-coho-auth-code">
                    <div className="text-txt-muted text-xs">Companies House Auth Code</div>
                    <div className="text-white font-mono">{cohoCode}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* BM-H500: the SDLT generate-link/status panel was imported but
            never mounted, so fee earners had no on-page way to reach the
            SDLT questionnaire tool or the tax-reference details (NI
            number / UTR-VAT / CoHo auth code) captured at onboarding.
            Mounted here, keyed off the same matter_reference resolution
            MoAHistoryTab below already uses. */}
        {submission?.id && (submission?.matter_reference || submission?.data?.Q4) && (
          <SDLTLinkSection
            submissionId={submission.id}
            data={submission.data}
            matterRef={submission?.matter_reference || submission?.data?.Q4}
          />
        )}

        {/* Brief 51.1 (14 Jul 2026) — MoA History section. Session-authed
            on the backend so fee earners see their own matter's ledger
            without an accounts-team escalation. Only mounts once a
            matter_reference has resolved on the loaded submission. */}
        {(submission?.matter_reference || submission?.data?.Q4) && (
          <MoAHistoryTab
            matterReference={submission?.matter_reference || submission?.data?.Q4}
          />
        )}
      </main>

      {/* feat/allocate-fee-earner — director-only dialog. Rendered at the
          end of the component so it's a sibling of the banner / panels,
          not nested inside any conditional sub-section. */}
      <Dialog open={allocFeOpen} onOpenChange={setAllocFeOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Allocate Fee Earner</DialogTitle>
            <DialogDescription>
              Director-only. Sets the responsible fee earner for this
              matter permanently — overrides any value coming from the
              Pre-CaseMinder Q6 fallback.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Fee earner
            </label>
            <select
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
              value={allocFeSelected}
              onChange={(e) => setAllocFeSelected(e.target.value)}
              disabled={allocFeLoading || allocFeSubmitting}
              data-testid="allocate-fee-earner-select"
            >
              <option value="">
                {allocFeLoading ? "Loading…" : "— Select a fee earner —"}
              </option>
              {allocFeOptions.map((fe) => (
                <option key={fe.email || fe.name} value={fe.name}>
                  {fe.name}
                  {fe.email ? ` (${fe.email})` : ""}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500">
              The list comes from staff_config (active fee earners only).
              If a name is missing here, add it to staff_config first.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAllocFeOpen(false)}
              disabled={allocFeSubmitting}
              data-testid="allocate-fee-earner-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitAllocateFeeEarner}
              disabled={!allocFeSelected || allocFeSubmitting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="allocate-fee-earner-submit"
            >
              {allocFeSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Allocating…
                </>
              ) : (
                "Allocate"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bonsai Mate — right-rail embedded on ≥1280px, slide-over below.
          Scoped to this matter's reference (FORF Section 1 Q4 or the matter ref).
          Chat session is mate_${userId}_matter_${matterRef}, suggestions filtered,
          picker locked, today-queue suppressed, history bound to this matter. */}
      <BonsaiMate
        context="matter"
        matterReference={submission?.data?.Q4 || submission?.matter_reference || submission?.secondary_label || ""}
      />
    </div>
  );
}
